import React, { useCallback, useEffect, useState } from 'react'
import {
  Button, Form, Image, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Typography, Upload, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { UploadProps } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { get, patch, post, del } from '../../lib/request'
import { errMsg, fmtTime } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'

const { Text } = Typography

export interface ProjectItem {
  id: number
  name: string
  nameEn?: string | null
  description?: string | null
  descriptionEn?: string | null
  logoUrl?: string | null
  coverUrl?: string | null
  category?: string | null
  status: 'active' | 'inactive'
  sortOrder: number
  _count?: { pages: number }
  createdAt?: string
  updatedAt?: string
}

const STATUS_LABELS = {
  active: { label: '启用', color: 'success' },
  inactive: { label: '停用', color: 'default' },
}

interface ProjectFormValue {
  name: string
  nameEn?: string
  description?: string
  descriptionEn?: string
  logoUrl?: string
  coverUrl?: string
  category?: string
  sortOrder: number
}

/** 项目类别选项（与后端 projects.ts 白名单一致：office|rental|retail|other） */
const CATEGORY_OPTIONS = [
  { value: 'office', label: '写字楼' },
  { value: 'rental', label: '公寓' },
  { value: 'retail', label: '商铺' },
  { value: 'other', label: '其他' },
]

const ProjectsPage: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = hasPermission(role, 'CONTENT_EDIT')

  const [list, setList] = useState<ProjectItem[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<ProjectItem | 'new' | null>(null)
  const [form] = Form.useForm<ProjectFormValue>()
  const [saving, setSaving] = useState(false)

  const fetchList = useCallback(() => {
    setLoading(true)
    get<ProjectItem[]>('/admin/projects')
      .then((d) => setList(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, '项目列表加载失败')))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { fetchList() }, [fetchList])

  const openEdit = (p?: ProjectItem) => {
    if (p) {
      setEditing(p)
      form.setFieldsValue({
        name: p.name,
        nameEn: p.nameEn ?? undefined,
        description: p.description ?? undefined,
        descriptionEn: p.descriptionEn ?? undefined,
        logoUrl: p.logoUrl ?? undefined,
        coverUrl: p.coverUrl ?? undefined,
        category: p.category ?? undefined,
        sortOrder: p.sortOrder ?? 0,
      })
    } else {
      setEditing('new')
      form.resetFields()
    }
  }

  const save = async () => {
    const v = await form.validateFields()
    const payload = {
      name: v.name,
      nameEn: v.nameEn?.trim() || undefined,
      description: v.description?.trim() || undefined,
      descriptionEn: v.descriptionEn?.trim() || undefined,
      logoUrl: v.logoUrl?.trim() || undefined,
      coverUrl: v.coverUrl?.trim() || undefined,
      category: v.category || undefined,
      sortOrder: v.sortOrder ?? 0,
    }
    setSaving(true)
    try {
      if (editing === 'new') {
        await post('/admin/projects', payload)
        message.success('项目已新建')
      } else if (editing) {
        await patch(`/admin/projects/${editing.id}`, payload)
        message.success('项目已更新')
      }
      setEditing(null)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  /** 封面图上传：走通用图片上传端点 /admin/uploads/image（同 ProjectIntro 与已删除的 ReferredBenefits 页（thankyou_v1）。
   *  成功后把返回的相对 URL 回填进 coverUrl；该封面同时用作小程序首页项目左图与分享卡片封面（两处联动）。 */
  const customRequestCover: NonNullable<UploadProps['customRequest']> = async (options) => {
    const { file, onSuccess, onError } = options
    const formData = new FormData()
    formData.append('file', file as File)
    try {
      const data = await post<{ url: string }>('/admin/uploads/image', formData, {
        headers: { 'Content-Type': undefined },
      })
      if (!data?.url) throw new Error('上传返回缺少 url')
      form.setFieldValue('coverUrl', data.url)
      onSuccess?.({ data })
      message.success('封面图已上传')
    } catch (err: unknown) {
      message.error(errMsg(err, '上传失败'))
      onError?.(err instanceof Error ? err : new Error('上传失败'))
    }
  }

  const deleteProject = (r: ProjectItem) => {
    del(`/admin/projects/${r.id}`)
      .then(() => {
        message.success('项目已删除')
        fetchList()
      })
      .catch((err: unknown) => message.error(errMsg(err, '删除失败')))
  }

  const columns: ColumnsType<ProjectItem> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 64, render: (v: number) => `#${v}` },
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (v: string) => <Text strong>{v}</Text>,
    },
    {
      title: 'Logo',
      dataIndex: 'logoUrl',
      key: 'logoUrl',
      width: 120,
      render: (v: string | null | undefined) =>
        v ? (
          <img 
            src={v} 
            alt={v} 
            style={{ width: 72, height: 48, objectFit: 'cover', borderRadius: 6 }}
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
            }}
          />
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: '封面图',
      dataIndex: 'coverUrl',
      key: 'coverUrl',
      width: 110,
      render: (v: string | null | undefined) =>
        v ? (
          <Image
            src={v}
            alt="封面"
            style={{ width: 88, height: 56, objectFit: 'cover', borderRadius: 6 }}
            preview={{ mask: false }}
            fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4OCIgaGVpZ2h0PSI1NiI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2VlZSIvPjwvc3ZnPg=="
          />
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: '类别',
      dataIndex: 'category',
      key: 'category',
      width: 90,
      render: (v: string | null | undefined) => {
        const opt = CATEGORY_OPTIONS.find((o) => o.value === v)
        return v ? <Tag color="green">{opt?.label ?? v}</Tag> : <Text type="secondary">—</Text>
      },
    },
    {
      title: '落地页数',
      key: 'pageCount',
      width: 80,
      render: (_: unknown, r) => r._count?.pages ?? 0,
    },
    { title: '排序值', dataIndex: 'sortOrder', key: 'sortOrder', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 96,
      render: (v: 'active' | 'inactive') => (
        <Tag color={STATUS_LABELS[v]?.color ?? 'default'}>{STATUS_LABELS[v]?.label ?? v}</Tag>
      ),
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 140,
      render: (v?: string) => fmtTime(v),
    },
    ...(canEdit
      ? [{
          title: '操作',
          key: 'actions',
          fixed: 'right' as const,
          width: 180,
          render: (_: unknown, r: ProjectItem) => (
            <Space size={4} wrap>
              <Button size="small" style={{ minHeight: 32 }} onClick={() => openEdit(r)}>编辑</Button>
              <Popconfirm
                title="删除该项目？"
                description="删除后不可恢复，请确认"
                okText="删除"
                cancelText="取消"
                onConfirm={() => deleteProject(r)}
              >
                <Button size="small" danger style={{ minHeight: 32 }}>删除</Button>
              </Popconfirm>
            </Space>
          ),
        }]
      : []),
  ]

  return (
    <>
      {/* merge_projects_into_projectintro_v1：本组件已作为「项目介绍」页的一个标签页渲染，
          页面级 Alert 已统一说明两个标签的分工，此处不再重复第二条 info 提示（对齐设计稿）。 */}
      <Space style={{ marginBottom: 14 }} wrap>
        {canEdit && (
          <Button type="primary" onClick={() => openEdit()} style={{ minHeight: 44 }}>
            新建项目
          </Button>
        )}
      </Space>
      <Table<ProjectItem>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12 }}
        pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
      />
      <Modal
        title={editing === 'new' ? '新建项目' : '编辑项目'}
        open={!!editing}
        onOk={save}
        onCancel={() => setEditing(null)}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        destroyOnClose
        width={600}
      >
        <Form<ProjectFormValue>
          form={form}
          layout="vertical"
          initialValues={{ sortOrder: 0 }}
        >
          <Form.Item
            name="name"
            label="项目名称"
            rules={[
              { required: true, message: '项目名称必填' },
              { max: 100, message: '项目名称不超过 100 字' },
            ]}
          >
            <Input placeholder="例：德高信中央大道" maxLength={100} showCount />
          </Form.Item>
          <Form.Item name="nameEn" label="项目英文名（选填 · 小程序英文模式显示）">
            <Input maxLength={100} placeholder="留空则英文模式仍显示中文" />
          </Form.Item>
          <Form.Item name="description" label="项目描述（选填）">
            <Input.TextArea rows={3} placeholder="项目简介" />
          </Form.Item>
          <Form.Item name="descriptionEn" label="项目英文描述（选填 · 小程序英文模式显示）">
            <Input.TextArea rows={3} maxLength={500} placeholder="留空则英文模式仍显示中文" />
          </Form.Item>
          <Form.Item name="logoUrl" label="Logo URL（选填）">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="category"
            label="项目类别（选填）"
            extra="首页项目卡片与列表按类别展示标签"
          >
            <Select allowClear placeholder="选择类别" options={CATEGORY_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="coverUrl"
            label="封面图片（选填）"
            extra="上传后同时用作小程序首页项目左图与分享卡片封面，两处联动一致"
          >
            <Input placeholder="上传后自动回填，或粘贴 https://... / /uploads/..." />
          </Form.Item>
          <Form.Item label="上传封面图">
            <Upload
              accept="image/*"
              maxCount={1}
              showUploadList={false}
              customRequest={customRequestCover}
            >
              <Button icon={<UploadOutlined />} style={{ minHeight: 40 }}>选择图片上传</Button>
            </Upload>
            <Form.Item noStyle shouldUpdate={(a, b) => a.coverUrl !== b.coverUrl}>
              {({ getFieldValue }) =>
                getFieldValue('coverUrl') ? (
                  <Image
                    src={getFieldValue('coverUrl')}
                    alt="封面预览"
                    style={{ marginTop: 10, width: 200, height: 128, objectFit: 'cover', borderRadius: 8 }}
                    preview={{ mask: false }}
                  />
                ) : null
              }
            </Form.Item>
          </Form.Item>
          <Form.Item name="sortOrder" label="排序值（越小越靠前）">
            <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default ProjectsPage
